/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — whole-zone spacing bar.

   Moves an entire NBZ (non-body zone) as a unit, like Word's header/footer
   "distance from edge" — the same interaction the classic canvas offers
   (CanvasComponents.tsx's ZoneSpacer, kept frozen there per contract fence)
   reimplemented against the Studio's draft (`edit`) instead of app.tsx
   state. Per-token spacing (the sb:/sa: mods, TokenComponents.tsx) stays
   for fine control on one chip; this bar is the whole-band nudge.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import { ptToPx } from '../../shared/body-layout';
import type { FormaEdit } from '../designer/DesignPalettes';

export type ZoneSpaceRegion = 'header' | 'pre' | 'post' | 'footer' | 'eos';

export function ZoneSpacer({
  forma,
  region,
  edge,
  onEdit,
}: {
  forma: StandardConfig;
  region: ZoneSpaceRegion;
  edge: 'before' | 'after';
  onEdit: FormaEdit;
}) {
  const sp = forma.special || ({} as StandardConfig['special']);
  const legacy =
    region === 'eos' && edge === 'before' ? (sp.eosSpacing ?? sp.postBodySpacing ?? 24) : 0;
  const cur = sp.zoneSpace?.[region]?.[edge] ?? legacy;
  const [hover, setHover] = React.useState(false);
  const [drag, setDrag] = React.useState(false);
  const lit = hover || drag;
  const dragMax = region === 'eos' && edge === 'before' ? Math.max(30, cur) : 120;

  function commit(v: number, max: number) {
    const nv = Math.max(0, Math.min(max, Math.round(v)));
    onEdit((f) => {
      const zs = { ...(f.special?.zoneSpace || {}) };
      zs[region] = { ...(zs[region] || {}), [edge]: nv };
      const next: StandardConfig = { ...f, special: { ...f.special, zoneSpace: zs } };
      if (region === 'eos' && edge === 'before') next.special = { ...next.special, eosSpacing: nv };
      return next;
    });
  }

  function startDrag(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const startVal = cur;
    setDrag(true);
    const onMove = (ev: MouseEvent) => commit(startVal + (ev.clientY - startY) * 0.5, dragMax);
    const onUp = () => {
      setDrag(false);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }

  const gapPx = ptToPx(cur);
  const label = `${region === 'eos' ? 'End of section' : region} — space ${edge}`;

  return (
    <div
      className="sfs-zonespacer"
      style={{ height: Math.max(gapPx, 0), width: '100%', position: 'relative', flexShrink: 0 }}
    >
      <div
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuenow={cur}
        aria-valuemin={0}
        aria-valuemax={dragMax}
        title={`${label} — drag, or set in the panel (${cur}pt)`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onMouseDown={startDrag}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            commit(cur + 1, dragMax);
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            commit(cur - 1, dragMax);
          }
        }}
        className={`sfs-zonespacer-grip${lit ? ' is-lit' : ''}`}
      >
        {lit && <span className="sfs-zonespacer-readout">{cur}pt</span>}
      </div>
    </div>
  );
}
